import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { t } from '../lib/i18n';
import { getInitials, formatTimeAgo } from '../lib/utils';
import axios from 'axios';
import PostCard from '../components/feed/PostCard';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { ArrowLeft, Loader2, Send } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PostDetailPage() {
  const { postId } = useParams();
  const { user, token, language } = useAuth();
  const tr = t(language);
  const navigate = useNavigate();
  const location = useLocation();
  const commentInputRef = useRef(null);
  
  const [post, setPost] = useState(null);
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const fetchPost = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/posts/${postId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPost(response.data);
    } catch (error) {
      console.error('Failed to fetch post:', error);
      toast.error('Post not found');
      navigate(-1);
    }
  }, [postId, token, navigate]);

  const fetchComments = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/posts/${postId}/comments`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setComments(response.data);
    } catch (error) {
      console.error('Failed to fetch comments:', error);
    } finally {
      setLoading(false);
    }
  }, [postId, token]);

  useEffect(() => {
    fetchPost();
    fetchComments();
  }, [fetchPost, fetchComments]);

  // Focus comment input if navigated with focusComment state
  useEffect(() => {
    if (!loading && location.state?.focusComment && commentInputRef.current) {
      // Small delay to ensure DOM is ready
      setTimeout(() => {
        commentInputRef.current?.focus();
        commentInputRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 300);
    }
  }, [loading, location.state]);

  const handlePostUpdate = (id, updates) => {
    setPost(prev => ({ ...prev, ...updates }));
  };

  const handlePostDelete = () => {
    navigate(-1);
  };

  const handleSubmitComment = async (e) => {
    e.preventDefault();
    if (!newComment.trim() || submitting) return;

    setSubmitting(true);
    try {
      const response = await axios.post(`${API}/posts/${postId}/comments`, {
        content: newComment.trim()
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setComments(prev => [...prev, response.data]);
      setPost(prev => ({ ...prev, comments_count: prev.comments_count + 1 }));
      setNewComment('');
    } catch (error) {
      toast.error(tr('error'));
    } finally {
      setSubmitting(false);
    }
  };

  if (loading || !post) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto pb-20" data-testid="post-detail-page">
      {/* Header */}
      <header className="sticky top-0 z-30 bg-background/80 backdrop-blur-xl border-b border-border/50 px-4 py-3">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" className="rounded-full" onClick={() => navigate(-1)}>
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <h1 className="font-bold text-lg">Post</h1>
        </div>
      </header>

      {/* Post */}
      <PostCard
        post={post}
        onUpdate={handlePostUpdate}
        onDelete={handlePostDelete}
      />

      {/* Comments Section */}
      <div className="border-t border-border/40">
        <div className="px-4 py-3 border-b border-border/40">
          <h2 className="font-semibold">{tr('comments')} ({comments.length})</h2>
        </div>

        {/* Comments List */}
        <div className="divide-y divide-border/40">
          {comments.map(comment => (
            <div key={comment.id} className="p-4" data-testid={`comment-${comment.id}`}>
              <div className="flex gap-3">
                <Avatar className="w-10 h-10">
                  <AvatarImage src={comment.avatar} alt={comment.display_name} />
                  <AvatarFallback className="bg-primary/10 text-primary">
                    {getInitials(comment.display_name || comment.username)}
                  </AvatarFallback>
                </Avatar>
                
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <span className="font-semibold">{comment.display_name || comment.username}</span>
                    <span className="text-muted-foreground">@{comment.username}</span>
                    <span className="text-muted-foreground">·</span>
                    <span className="text-sm text-muted-foreground">
                      {formatTimeAgo(comment.created_at, language)}
                    </span>
                  </div>
                  <p className="mt-1">{comment.content}</p>
                </div>
              </div>
            </div>
          ))}

          {comments.length === 0 && (
            <div className="py-8 text-center text-muted-foreground">
              No comments yet
            </div>
          )}
        </div>
      </div>

      {/* Comment Input - Fixed at bottom */}
      <form 
        onSubmit={handleSubmitComment}
        className="fixed bottom-16 lg:bottom-0 left-0 right-0 lg:left-64 bg-background border-t border-border/50 p-4 z-40"
      >
        <div className="max-w-2xl mx-auto flex gap-2">
          <Avatar className="w-10 h-10 shrink-0">
            <AvatarImage src={user?.avatar} alt={user?.display_name} />
            <AvatarFallback className="bg-primary/10 text-primary">
              {getInitials(user?.display_name || user?.username)}
            </AvatarFallback>
          </Avatar>
          <Input
            ref={commentInputRef}
            value={newComment}
            onChange={(e) => setNewComment(e.target.value)}
            placeholder="Write a comment..."
            className="h-10 rounded-full bg-secondary/50"
            data-testid="comment-input"
          />
          <Button 
            type="submit"
            size="icon"
            className="w-10 h-10 rounded-full shrink-0"
            disabled={!newComment.trim() || submitting}
            data-testid="submit-comment-btn"
          >
            {submitting ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Send className="w-4 h-4" />
            )}
          </Button>
        </div>
      </form>
    </div>
  );
}
